import { useCallback, useEffect, useState } from "react";

import { CARRIER_CHECK_FAILED_MESSAGE, emailIsValid } from "utils";

import { useAPI } from "./useAPI";
import { useDebounce } from "./useDebounce";

const CARRIER_EXISTS_DEBOUNCE_MS = 400;

interface CarrierExistsResponse {
  exists: boolean;
  // The DIRECT_BOOKING_ASSIGNEE_* rejection the post would answer with.
  code: string | null;
}

interface UseCarrierExistsProps {
  // Null skips the request.
  email: string | null;
}

export interface UseCarrierExistsResult {
  // Null while unknown or loading.
  carrierExists: boolean | null;
  // DIRECT_BOOKING_ASSIGNEE_* when carrierExists is false.
  rejectionCode: string | null;
  isLoading: boolean;
  error: string | null;
  // Asks again for the same email, after a failed check.
  retry: () => void;
}

// The last answer, with the email and attempt it answers for.
interface CarrierExistsAnswer {
  email: string;
  attempt: number;
  carrierExists: boolean | null;
  rejectionCode: string | null;
  error: string | null;
}

/*
Asks POST /carrier-exists whether the shipper can post a shipment already booked
to the carrier behind an email. The answer covers every rejection the post applies
(no account, a shipper's email, no company, disabled, blocked), not only whether
an account exists. The answer is kept with the email it was given for, so a late
answer for an earlier email can never validate the current one. This needs each
request's own email on failure too, which useWriterComponentState's single
onWriteFailure callback can't give, so the hook calls the API directly.
*/
export function useCarrierExists(
  props: UseCarrierExistsProps,
): UseCarrierExistsResult {
  const { email } = props;
  const { api } = useAPI();
  const debouncedEmail = useDebounce({
    value: email,
    delay: CARRIER_EXISTS_DEBOUNCE_MS,
  });
  const [answer, setAnswer] = useState<CarrierExistsAnswer | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  useEffect(() => {
    if (debouncedEmail === null || !emailIsValid(debouncedEmail)) {
      return undefined;
    }
    const requestedEmail = debouncedEmail;
    /*
    There is one answer slot, so a superseded request landing after the one that
    replaced it would overwrite a good answer with a stale one. The reader below
    would then refuse it for the wrong email and report a check still running,
    with nothing left in flight to finish it.
    */
    let superseded = false;
    api<CarrierExistsResponse>("post", "/carrier-exists", {
      email: requestedEmail.toLowerCase(),
    })
      .then((response) => {
        if (superseded) {
          return;
        }
        setAnswer({
          email: requestedEmail,
          attempt,
          carrierExists: response.data.exists,
          rejectionCode: response.data.code,
          error: null,
        });
      })
      .catch(() => {
        if (superseded) {
          return;
        }
        setAnswer({
          email: requestedEmail,
          attempt,
          carrierExists: null,
          rejectionCode: null,
          error: CARRIER_CHECK_FAILED_MESSAGE,
        });
      });
    return () => {
      superseded = true;
    };
  }, [api, attempt, debouncedEmail]);

  const emailIsCheckable = email !== null && emailIsValid(email);
  if (
    !emailIsCheckable ||
    answer?.email !== email ||
    answer.attempt !== attempt
  ) {
    return {
      carrierExists: null,
      rejectionCode: null,
      isLoading: emailIsCheckable,
      error: null,
      retry,
    };
  }
  return {
    carrierExists: answer.carrierExists,
    rejectionCode: answer.rejectionCode,
    isLoading: false,
    error: answer.error,
    retry,
  };
}
